import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { BehaviorSubject, timer } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { WebsocketLifecycleService } from '@core/realtime/websocket';
import { AuthorisationStateService, HcService } from '@project/services';
import { TranslateService } from '@project/translate';
import { IAuthResult } from '@project/view-models';

enum EPeopleInQueueStatuses {
  Few = 'few',
  Medium = 'medium',
  Lot = 'lot',
}

const ONE_HOUR_IN_SECONDS = 60 * 60; // 1 hour
const QUEUE_DELAY_TICK_INTERVAL_MS = 60 * 1000; // 1 minute

interface IQueueState {
  count: number;
  seconds: number | null;
  receivedAt: number;
}

type TQueueDelayView =
  | { type: 'normalized' }
  | { type: 'delayed'; hours: number }
  | {
      type: 'none';
    };

@Component({
  selector: 'app-people-in-queue-card',
  templateUrl: './people-in-queue-card.component.html',
  styleUrls: ['./people-in-queue-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeopleInQueueCardComponent implements OnChanges {
  @Input() peopleInQueueCount: number;
  @Input() healthCenter?: any;
  @Input() disabled?: boolean;
  @Input() oldestWaitSeconds: number | null = null;

  private readonly _queueState$ = new BehaviorSubject<IQueueState | null>(null);

  public readonly queueDelay$ = this._queueState$.pipe(
    filter((state): state is IQueueState => state !== null),
    switchMap((state) => timer(0, QUEUE_DELAY_TICK_INTERVAL_MS).pipe(map(() => this.buildDelayView(state)))),
  );

  constructor(
    private authorizationStateService: AuthorisationStateService,
    public hcService: HcService,
    private websocketLifecycicleService: WebsocketLifecycleService,
  ) {}

  ngOnChanges(): void {
    this._queueState$.next({
      count: this.peopleInQueueCount,
      seconds: this.oldestWaitSeconds ?? null,
      receivedAt: Date.now(),
    });
  }

  get queueStatus(): EPeopleInQueueStatuses {
    return this.calculateQueueStatus(this.peopleInQueueCount);
  }

  private buildDelayView(state: IQueueState): TQueueDelayView {
    if (state.count === 0) {
      return { type: 'normalized' };
    }

    if (state.seconds === null) {
      return { type: 'none' };
    }

    const elapsedSeconds = Math.floor((Date.now() - state.receivedAt) / 1000);
    const currentSeconds = state.seconds + elapsedSeconds;

    if (currentSeconds >= ONE_HOUR_IN_SECONDS) {
      return { type: 'delayed', hours: Math.floor(currentSeconds / ONE_HOUR_IN_SECONDS) };
    }

    return { type: 'none' };
  }

  private calculateQueueStatus(peopleInQueue: number): EPeopleInQueueStatuses {
    if (typeof peopleInQueue !== 'number') {
      return null;
    }

    if (peopleInQueue < 5) {
      return EPeopleInQueueStatuses.Few;
    }

    if (peopleInQueue < 12) {
      return EPeopleInQueueStatuses.Medium;
    }

    return EPeopleInQueueStatuses.Lot;
  }

  public impersonate(hcId: string) {
    this.authorizationStateService.impersonateHealthCenter(hcId).subscribe(
      (authResult: IAuthResult) => {
        this.hcService.switchHC(hcId);
        this.websocketLifecycicleService.reconnect();
      },
      (error) => console.log(error),
    );
  }

  public inUseHc(hcId: string, currentHcId: string) {
    return hcId === currentHcId;
  }

  public getButtonText(hcId: string, currentHcId: string) {
    if (hcId === currentHcId) {
      return TranslateService.localize('session.hc-in-use');
    } else {
      return TranslateService.localize('session.use-this-hc');
    }
  }
}
